// BUILD-042 (Oct 16) — the pure logic behind the expanded-fields "Set up signing" editor.
//
// Everything works in PDF POINTS (1/72 in), origin at the page's BOTTOM-LEFT, 1-based pages: exactly what the server stores and
// validates. No DOM here, so the real screen logic is tested under plain Node (formFieldsModel.test.mjs). The SERVER is the authority
// (lib/lambda/hr/form-field-model.ts re-validates everything against the stored PDF); this module only keeps HR from building a setup
// the server would refuse, and says why in plain words. Nothing is silently changed at publish time.
import type { HrFormField, HrFormFieldKind, HrInitialsSet, HrPlacementBox, HrImportReport, HrImportResolution } from '../../api/client';

export type UiField = HrFormField;
export type UiInitialsSet = HrInitialsSet;
export type FieldKind = HrFormFieldKind;
export type Box = HrPlacementBox;
export interface PagePts { width: number; height: number }

// Mirrors of the server's size rules (form-field-model.ts MIN_BOX / MAX_FRACTION). The server stays the authority.
export const KIND_MIN: Record<FieldKind, [number, number]> = {
  signature: [72, 20], initial: [24, 16], dateSigned: [48, 12], text: [24, 12], dropdown: [48, 14], checkbox: [8, 8], radio: [8, 8],
};
export const KIND_MAX_FRACTION: Record<FieldKind, [number, number]> = {
  signature: [0.8, 0.25], initial: [0.3, 0.15], dateSigned: [0.5, 0.12], text: [0.95, 0.9], dropdown: [0.95, 0.2], checkbox: [0.1, 0.1], radio: [0.1, 0.1],
};
export const KIND_DEFAULT_SIZE: Record<FieldKind, [number, number]> = {
  signature: [200, 40], initial: [60, 24], dateSigned: [110, 20], text: [200, 22], dropdown: [140, 22], checkbox: [14, 14], radio: [14, 14],
};
export const KIND_LABEL: Record<FieldKind, string> = {
  signature: 'Signature', initial: 'Initials', dateSigned: 'Date signed', text: 'Text box', dropdown: 'Dropdown', checkbox: 'Checkbox', radio: 'Choice option',
};
export const KIND_ORDER: FieldKind[] = ['signature', 'initial', 'dateSigned', 'text', 'dropdown', 'checkbox', 'radio'];
export const MAX_LABEL_LENGTH = 120;
export const MAX_OPTION_LENGTH = 80;
export const MAX_DROPDOWN_OPTIONS = 30;

const round2 = (n: number) => Math.round(n * 100) / 100;
const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), Math.max(lo, hi));
const KEY_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

export function boxesOverlap(a: Box, b: Box): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}
export function maxSize(kind: FieldKind, page: PagePts): { width: number; height: number } {
  const [wf, hf] = KIND_MAX_FRACTION[kind];
  return { width: Math.floor(page.width * wf * 100) / 100, height: Math.floor(page.height * hf * 100) / 100 };
}

// ── initials (one anchor, selected or all pages, exclusions, per-page overrides) ──────────────────────────────────────────
export function initialsPageList(set: UiInitialsSet, pageCount: number): number[] {
  const base = set.pages === 'all' ? Array.from({ length: pageCount }, (_, i) => i + 1) : [...set.pages];
  const ex = new Set(set.excludePages);
  return base.filter((n) => n >= 1 && n <= pageCount && !ex.has(n)).sort((a, b) => a - b);
}
export function expandInitialsSet(set: UiInitialsSet, pageCount: number): UiField[] {
  return initialsPageList(set, pageCount).map((page) => {
    const ov = set.overrides.find((o) => o.page === page);
    return {
      fieldKey: `${set.setKey}_p${page}`, kind: 'initial' as const, page, box: { ...(ov ? ov.box : set.anchor) },
      label: set.label, required: set.required, source: 'employee' as const,
      origin: { kind: ov ? 'initials-override' as const : 'initials-set' as const, setKey: set.setKey },
    };
  });
}
export function allFields(fields: UiField[], sets: UiInitialsSet[], pageCount: number): UiField[] {
  return [...fields, ...sets.flatMap((s) => expandInitialsSet(s, pageCount))];
}

/** "1,3-5" -> [1,3,4,5]. Plain error text for anything unreadable or out of range. */
export function parsePageList(text: string, pageCount: number): { ok: true; pages: number[] } | { ok: false; error: string } {
  const out = new Set<number>();
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return { ok: false, error: 'Enter page numbers such as 1, 3-5.' };
  for (const p of parts) {
    const m = /^(\d{1,3})(?:\s*-\s*(\d{1,3}))?$/.exec(p);
    if (!m) return { ok: false, error: `"${p}" is not a page number or a range like 3-5.` };
    const a = Number(m[1]); const b = m[2] ? Number(m[2]) : a;
    if (a < 1 || b < a || b > pageCount) return { ok: false, error: `Pages must be between 1 and ${pageCount}.` };
    for (let n = a; n <= b; n++) out.add(n);
  }
  return { ok: true, pages: [...out].sort((x, y) => x - y) };
}
export function formatPageList(pages: number[]): string {
  const s = [...new Set(pages)].sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < s.length;) {
    let j = i; while (j + 1 < s.length && s[j + 1] === s[j] + 1) j++;
    out.push(j > i ? `${s[i]}-${s[j]}` : String(s[i])); i = j + 1;
  }
  return out.join(', ');
}

// ── creating fields ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export function uniqueKey(base: string, used: Set<string>): string {
  const root = (base.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '') || 'field').replace(/^(?=\d)/, 'f_').slice(0, 52);
  let k = root; let n = 2;
  while (used.has(k)) k = `${root}_${n++}`;
  used.add(k);
  return k;
}
export const usedKeys = (fields: UiField[], sets: UiInitialsSet[]): Set<string> => new Set([...fields.map((f) => f.fieldKey), ...sets.map((s) => s.setKey)]);

/** First free spot on the page for a new box of this size (scans from upper-middle downward, then across). */
export function findFreeBox(page: PagePts, width: number, height: number, occupied: Box[]): Box {
  const w = Math.min(width, page.width - 36); const h = Math.min(height, page.height - 36);
  for (let y = Math.round(page.height * 0.62); y >= 36; y -= Math.max(10, Math.round(h) + 8)) {
    for (let x = 54; x + w <= page.width - 18; x += Math.max(30, Math.round(w) + 12)) {
      const b = { x, y, width: w, height: h };
      if (!occupied.some((o) => boxesOverlap(o, b))) return b;
    }
  }
  return { x: round2((page.width - w) / 2), y: round2(page.height * 0.5), width: w, height: h };
}
export interface NewFieldContext { page: number; pages: PagePts[]; fields: UiField[]; sets: UiInitialsSet[] }

export function newField(kind: FieldKind, ctx: NewFieldContext): UiField {
  const pg = ctx.pages[ctx.page - 1];
  const [w, h] = KIND_DEFAULT_SIZE[kind];
  const box = findFreeBox(pg, w, h, allFields(ctx.fields, ctx.sets, ctx.pages.length).filter((f) => f.page === ctx.page).map((f) => f.box));
  const keys = usedKeys(ctx.fields, ctx.sets);
  const key = uniqueKey(kind === 'dateSigned' ? 'date_signed' : kind === 'signature' ? 'employee_signature' : kind === 'radio' ? 'choice' : kind, keys);
  const base: UiField = { fieldKey: key, kind, page: ctx.page, box: { x: round2(box.x), y: round2(box.y), width: round2(box.width), height: round2(box.height) }, label: KIND_LABEL[kind], required: kind === 'signature' || kind === 'dateSigned', source: 'employee', origin: { kind: 'authored' } };
  if (kind === 'text') return { ...base, label: 'Text answer', text: { multiline: false, maxLength: 100, validation: 'none' } };
  if (kind === 'dropdown') return { ...base, label: 'Choose one', dropdown: { options: ['Option 1', 'Option 2'] } };
  if (kind === 'checkbox') return { ...base, label: 'I agree' };
  return base;
}
/** A single-choice group is always created with two options side by side; HR renames them and may add more. */
export function newRadioGroup(ctx: NewFieldContext): UiField[] {
  const pg = ctx.pages[ctx.page - 1];
  const [w, h] = KIND_DEFAULT_SIZE.radio;
  const occupied = allFields(ctx.fields, ctx.sets, ctx.pages.length).filter((f) => f.page === ctx.page).map((f) => f.box);
  const first = findFreeBox(pg, w * 8, h, occupied);
  const keys = usedKeys(ctx.fields, ctx.sets);
  const groupKey = uniqueKey('choice_group', new Set([...keys, ...ctx.fields.filter((f) => f.radio).map((f) => f.radio!.groupKey)]));
  return ['Option A', 'Option B'].map((opt, i) => ({
    fieldKey: uniqueKey(`${groupKey}_${i + 1}`, keys), kind: 'radio' as const, page: ctx.page,
    box: { x: round2(first.x + i * 100), y: round2(first.y), width: w, height: h },
    label: opt, required: true, source: 'employee' as const, radio: { groupKey, optionValue: opt }, origin: { kind: 'authored' as const },
  }));
}
export function addRadioOption(group: UiField[], ctx: NewFieldContext): UiField {
  const last = group[group.length - 1];
  const keys = usedKeys(ctx.fields, ctx.sets);
  const pg = ctx.pages[last.page - 1];
  const occupied = allFields(ctx.fields, ctx.sets, ctx.pages.length).filter((f) => f.page === last.page).map((f) => f.box);
  const wanted = { x: round2(last.box.x + 100), y: last.box.y, width: last.box.width, height: last.box.height };
  const box = wanted.x + wanted.width <= pg.width && !occupied.some((o) => boxesOverlap(o, wanted)) ? wanted : { ...findFreeBox(pg, last.box.width, last.box.height, occupied) };
  const n = group.length + 1;
  let opt = `Option ${String.fromCharCode(64 + n)}`; const names = new Set(group.map((g) => g.radio!.optionValue));
  while (names.has(opt)) opt += '2';
  return { ...last, fieldKey: uniqueKey(`${last.radio!.groupKey}_${n}`, keys), box: { x: round2(box.x), y: round2(box.y), width: box.width, height: box.height }, label: opt, radio: { groupKey: last.radio!.groupKey, optionValue: opt } };
}
export function newInitialsSet(ctx: NewFieldContext): UiInitialsSet {
  const pg = ctx.pages[ctx.page - 1];
  const [w, h] = KIND_DEFAULT_SIZE.initial;
  const keys = usedKeys(ctx.fields, ctx.sets);
  const setKey = uniqueKey('initials', keys);
  // bottom-right corner, where page initials are normally written; HR can move any single page afterwards
  const anchor = { x: round2(Math.max(18, pg.width - w - 36)), y: 36, width: w, height: h };
  return { setKey, label: 'Initials', required: true, anchor, pages: 'all', excludePages: [], overrides: [] };
}

// ── moving / resizing ───────────────────────────────────────────────────────────────────────────────────────────────────────
export function moveBox(box: Box, dxPts: number, dyPtsDown: number, page: PagePts): Box {
  return { ...box, x: round2(clamp(box.x + dxPts, 0, page.width - box.width)), y: round2(clamp(box.y - dyPtsDown, 0, page.height - box.height)) };
}
export function resizeBox(box: Box, dxPts: number, dyPtsDown: number, page: PagePts, kind: FieldKind): Box {
  const [minW, minH] = KIND_MIN[kind]; const max = maxSize(kind, page);
  const top = box.y + box.height;
  const width = round2(clamp(box.width + dxPts, minW, Math.min(max.width, page.width - box.x)));
  const height = round2(clamp(box.height + dyPtsDown, minH, Math.min(max.height, top)));
  return { x: box.x, width, height, y: round2(top - height) };
}
export function nudgeBox(box: Box, key: string, shift: boolean, big: boolean, page: PagePts, kind: FieldKind): Box {
  const step = big ? 10 : 1;
  const d = ({ ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] } as Record<string, [number, number]>)[key];
  if (!d) return box;
  return shift ? resizeBox(box, d[0], d[1], page, kind) : moveBox(box, d[0], d[1], page);
}
export function boxToCss(box: Box, page: PagePts, scale: number): { left: number; top: number; width: number; height: number } {
  return { left: box.x * scale, top: (page.height - box.y - box.height) * scale, width: box.width * scale, height: box.height * scale };
}
export function describeBox(page: number, box: Box): string {
  const inch = (pts: number) => (Math.round((pts / 72) * 10) / 10).toFixed(1);
  return `page ${page}, ${inch(box.x)} in from the left, ${inch(box.y)} in from the bottom, ${inch(box.width)} x ${inch(box.height)} in`;
}

// Mirror of the server's several-line envelope (lib/lambda/hr/form-field-model.ts MULTILINE_LIMITS). The envelope is exactly what a real
// sandbox signing demonstrated (a 361 x 101 pt text box with a 100-character limit kept two lines). The server stays the authority.
export const MULTILINE_LIMITS = { minBoxWidthPts: 360, minBoxHeightPts: 100, maxLines: 2, maxCharacters: 100 } as const;
export function multilineProblem(f: Pick<UiField, 'box' | 'source'> & { text?: UiField['text'] }): string | null {
  const L = MULTILINE_LIMITS;
  if (f.source !== 'employee') return 'a several-line box must be filled in by the employee (not locked, fixed or entered by HR).';
  if ((f.text?.validation ?? 'none') !== 'none') return 'a several-line box takes plain text. Choose "Any text" for what can be typed.';
  if ((f.text?.maxLength ?? 0) > L.maxCharacters) return `a several-line box allows at most ${L.maxCharacters} characters (the size the signing provider has been tested with). Lower the longest answer.`;
  if (f.box.width < L.minBoxWidthPts - 0.005 || f.box.height < L.minBoxHeightPts - 0.005) {
    return `a several-line box must be at least ${L.minBoxWidthPts} x ${L.minBoxHeightPts} points (about 5 x 1.4 inches), the size the signing provider has been tested with. Make the box larger, or use a single-line box.`;
  }
  return null;
}

// ── plain-language problems that would stop a publish (the server repeats every one of these checks) ──────────────────────
export function fieldProblems(f: UiField, pages: PagePts[], multilineConfirmed: boolean): string[] {
  const out: string[] = [];
  const who = `"${f.label || f.fieldKey}"`;
  if (!KEY_RE.test(f.fieldKey)) out.push(`${who}: the field name must start with a letter and use only letters, numbers and underscores.`);
  const pg = pages[f.page - 1];
  if (!pg) { out.push(`${who} is on a page that does not exist.`); return out; }
  const [minW, minH] = KIND_MIN[f.kind]; const max = maxSize(f.kind, pg);
  if (f.box.width < minW || f.box.height < minH) out.push(`${who} is too small (minimum ${minW} x ${minH} points).`);
  else if (f.box.width > max.width + 0.005 || f.box.height > max.height + 0.005) out.push(`${who} is too large for page ${f.page}.`);
  if (f.box.x < -0.005 || f.box.y < -0.005 || f.box.x + f.box.width > pg.width + 0.005 || f.box.y + f.box.height > pg.height + 0.005) out.push(`${who} extends past the edge of page ${f.page}.`);
  if (!f.label.trim()) out.push('Give every field a label so the employee knows what to enter.');
  else if (f.label.length > MAX_LABEL_LENGTH) out.push(`${who}: the label is longer than ${MAX_LABEL_LENGTH} characters.`);
  if (f.kind === 'text') {
    if (f.text?.multiline) {
      if (!multilineConfirmed) out.push(`${who}: several-line answers are not available.`);
      else { const why = multilineProblem(f); if (why) out.push(`${who}: ${why}`); }
    }
    if (!f.text || f.text.maxLength < 1 || f.text.maxLength > 1000) out.push(`${who}: the length limit must be between 1 and 1000 characters.`);
    if (f.source === 'profile' && !f.profileKey) out.push(`${who}: choose employee name or work email.`);
    if (f.source === 'fixed' && !f.fixedValue) out.push(`${who}: enter the fixed text.`);
    if (f.source === 'fixed' && f.text && f.fixedValue && f.fixedValue.length > f.text.maxLength) out.push(`${who}: the fixed text is longer than the length limit.`);
    if (f.source === 'employee' && f.startingValue && f.text && f.startingValue.length > f.text.maxLength) out.push(`${who}: the starting answer is longer than the length limit.`);
    if (f.source !== 'employee' && f.startingValue) out.push(`${who}: a starting answer belongs to a box the employee fills in.`);
    const legacy = legacyValueProblem(f); if (legacy) out.push(legacy);
    if (f.text?.validation === 'dateUS') {
      if (f.source === 'employee' || f.source === 'profile') out.push(`${who}: the date format is for values HR enters. For a date the employee fills in, use "Date signed".`);
      if (f.text.maxLength < 10) out.push(`${who}: a date needs a length limit of at least 10 characters.`);
      if (f.source === 'fixed' && f.fixedValue && !isRealUsDate(f.fixedValue)) out.push(`${who}: the fixed date must be a real date as MM/DD/YYYY, for example ${DATE_US_EXAMPLE}.`);
    }
  }
  if (f.kind === 'dropdown') {
    const o = f.dropdown?.options ?? [];
    if (o.length < 2 || o.length > MAX_DROPDOWN_OPTIONS) out.push(`${who}: a dropdown needs between 2 and ${MAX_DROPDOWN_OPTIONS} choices.`);
    else if (new Set(o).size !== o.length || o.some((x) => !x.trim() || x.length > MAX_OPTION_LENGTH)) out.push(`${who}: dropdown choices must be different, not empty, and short.`);
    const legacy = legacyValueProblem(f); if (legacy) out.push(legacy);
    if (f.dropdown?.defaultValue && !o.includes(f.dropdown.defaultValue)) out.push(`${who}: the starting choice is not one of the choices.`);
  }
  if (f.locked && f.source === 'employee') out.push(`${who}: a field the employee fills in cannot be locked.`);
  return out;
}

export function setupProblems(fields: UiField[], sets: UiInitialsSet[], pages: PagePts[], opts: { multilineConfirmed: boolean; maxFields: number }): string[] {
  const out: string[] = [];
  const all = allFields(fields, sets, pages.length);
  if (!all.some((f) => f.kind === 'signature')) out.push('Place at least one employee signature box.');
  if (all.length > opts.maxFields) out.push(`This form has ${all.length} fields. The most one form can have is ${opts.maxFields}.`);
  const seen = new Set<string>();
  for (const f of fields) {
    if (seen.has(f.fieldKey)) out.push(`Two fields share the name "${f.fieldKey}".`);
    seen.add(f.fieldKey);
    out.push(...fieldProblems(f, pages, opts.multilineConfirmed));
  }
  for (const s of sets) {
    const list = initialsPageList(s, pages.length);
    if (!list.length) { out.push(`Initials rule "${s.label}" leaves no pages to initial.`); continue; }
    for (const g of expandInitialsSet(s, pages.length)) out.push(...fieldProblems({ ...g, label: g.label || 'Initials' }, pages, opts.multilineConfirmed).filter((p) => !/share the name/.test(p)));
    if (seen.has(s.setKey)) out.push(`The initials rule name "${s.setKey}" is already used by a field.`);
  }
  // choice groups
  const groups = new Map<string, UiField[]>();
  for (const f of fields.filter((x) => x.kind === 'radio' && x.radio)) (groups.get(f.radio!.groupKey) ?? groups.set(f.radio!.groupKey, []).get(f.radio!.groupKey)!).push(f);
  for (const [g, m] of groups) {
    if (m.length < 2) out.push(`Choice group "${g}" needs at least two options.`);
    if (new Set(m.map((x) => x.radio!.optionValue)).size !== m.length) out.push(`Choice group "${g}": each option needs a different name.`);
    if (new Set(m.map((x) => x.required)).size > 1) out.push(`Choice group "${g}": every option must agree on required.`);
  }
  // overlaps, once per pair
  let n = 0;
  for (let i = 0; i < all.length && n < 10; i++) for (let j = i + 1; j < all.length && n < 10; j++) {
    if (all[i].page === all[j].page && boxesOverlap(all[i].box, all[j].box)) { out.push(`"${all[i].label}" and "${all[j].label}" overlap on page ${all[i].page}. Move one of them.`); n++; }
  }
  return [...new Set(out)].slice(0, 40);
}

/** What the screen sends: authored + imported fields and the initials RULES (initials boxes are regenerated by the server). */
export function toApiBody(fields: UiField[], sets: UiInitialsSet[]): { fields: UiField[]; initialsSets: UiInitialsSet[] } {
  return { fields: fields.map((f) => ({ ...f, box: { ...f.box } })), initialsSets: sets.map((s) => ({ ...s, anchor: { ...s.anchor }, overrides: s.overrides.map((o) => ({ page: o.page, box: { ...o.box } })) })) };
}
export function sameBody(a: { fields: UiField[]; initialsSets: UiInitialsSet[] }, b: { fields: UiField[]; initialsSets: UiInitialsSet[] }): boolean {
  const norm = (x: { fields: UiField[]; initialsSets: UiInitialsSet[] }) => JSON.stringify({ f: [...x.fields].sort((p, q) => p.fieldKey.localeCompare(q.fieldKey)), i: [...x.initialsSets].sort((p, q) => p.setKey.localeCompare(q.setKey)) });
  return norm(a) === norm(b);
}

export function summarize(fields: UiField[], sets: UiInitialsSet[], pageCount: number): { total: number; byKind: Record<string, number>; required: number; prefilled: number; locked: number } {
  const all = allFields(fields, sets, pageCount);
  const byKind: Record<string, number> = {};
  for (const f of all) byKind[f.kind] = (byKind[f.kind] || 0) + 1;
  return { total: all.length, byKind, required: all.filter((f) => f.required).length, prefilled: all.filter((f) => f.source !== 'employee').length, locked: all.filter((f) => f.locked).length };
}

export function sourceChoices(kind: FieldKind): Array<'employee' | 'profile' | 'assignment' | 'fixed'> {
  // Oct 18 D1: only a TEXT box can carry a value the employee may not change (the signing provider can only make text boxes read-only).
  if (kind === 'text') return ['employee', 'profile', 'assignment', 'fixed'];
  return ['employee'];
}
export const SOURCE_LABEL: Record<string, string> = {
  employee: 'The employee fills it in while signing (it can start with an answer you set)',
  profile: 'Copied from the employee record (name or work email) - read-only for the employee',
  assignment: 'HR types it when assigning the form - read-only for the employee',
  fixed: 'Fixed text I enter now (the same for everyone) - read-only for the employee',
};

/** Oct 18 D1: the PLAIN-WORDS contract the screen states next to a value source (the server enforces it; this only explains it). */
export const READ_ONLY_EXPLANATION = 'Whatever HR sets here is final: the employee sees it but cannot change it.';
export const STARTING_ANSWER_EXPLANATION = 'A starting answer is only a head start. The employee can change or clear it, so it is not a fixed value.';

/** A value HR (or the employee record) sets is final, so it is read-only by construction; only a text box can be. */
export function isAuthoritative(f: Pick<UiField, 'source'>): boolean { return f.source !== 'employee'; }

/**
 * A field saved BEFORE the read-only rule: a value set by HR that the employee could still change (an unlocked text box, or any HR-set dropdown).
 * Nothing rewrites it silently; HR picks one of the explicit conversions below, then publishes again.
 */
export function legacyValueProblem(f: UiField): string | null {
  if (f.source === 'employee') return null;
  if (f.kind === 'dropdown') return `"${f.label}": this dropdown was saved as a value HR sets, but the employee could still change it. Choose what it should be (below), then publish again.`;
  if (f.kind === 'text' && f.locked !== true) return `"${f.label}": this value was saved without being read-only, so the employee could change it. Choose read-only (HR's value is final) or a starting answer the employee may change (below), then publish again.`;
  return null;
}

/** Explicit HR choice: this value is final (read-only for the employee). */
export function makeReadOnly(f: UiField): UiField { return { ...f, locked: true, required: true }; }
/** Explicit HR choice: this was only a head start. The employee fills the box in; the old HR value becomes its editable starting answer. */
export function makeStartingAnswer(f: UiField): UiField {
  const next: UiField = { ...f, source: 'employee' };
  const start = f.kind === 'text' ? (f.fixedValue ?? '') : '';
  delete next.fixedValue; delete next.profileKey; delete next.locked;
  if (start) next.startingValue = start; else delete next.startingValue;
  return next;
}
/** Explicit HR choice for an HR-set dropdown: an EMPLOYEE dropdown that STARTS on the choice HR had set (the employee may change it). */
export function dropdownToEmployeeChoice(f: UiField): UiField {
  const chosen = f.fixedValue || f.dropdown?.defaultValue;
  const next: UiField = { ...f, source: 'employee', dropdown: { options: [...(f.dropdown?.options ?? [])], ...(chosen ? { defaultValue: chosen } : {}) } };
  delete next.fixedValue; delete next.locked; delete next.profileKey; delete next.startingValue;
  return next;
}
/** Explicit HR choice for an HR-set dropdown: a READ-ONLY TEXT BOX showing the chosen option (the reviewed, supported representation). */
export function dropdownToReadOnlyText(f: UiField): UiField {
  const chosen = f.fixedValue || f.dropdown?.defaultValue || f.dropdown?.options[0] || '';
  const next: UiField = { ...f, kind: 'text', source: 'fixed', fixedValue: chosen, locked: true, required: true, text: { multiline: false, maxLength: Math.min(1000, Math.max(20, chosen.length)), validation: 'none' } };
  delete next.dropdown; delete next.profileKey; delete next.startingValue;
  return next;
}

/**
 * A real calendar date written MM/DD/YYYY (mirror of the server's isRealUsDate; the server is the authority). String and integer
 * arithmetic only - no Date object, so no time zone or daylight-saving shift can change the characters HR typed.
 */
export const DATE_US_EXAMPLE = '03/07/2026';
export function isRealUsDate(value: string): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!m) return false;
  const month = Number(m[1]); const day = Number(m[2]); const year = Number(m[3]);
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}
/** The calendar picker returns YYYY-MM-DD; this converts by splitting the text (never through Date), so there is no time zone shift. */
export function isoToUs(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}
export function usToIso(us: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(us);
  return m && isRealUsDate(us) ? `${m[3]}-${m[1]}-${m[2]}` : '';
}
/** While typing: keep digits, add the slashes, stop at 10 characters. "3072026" -> "03/07/2026" is NOT guessed; only separators are inserted. */
export function maskUsDate(raw: string): string {
  const d = raw.replace(/[^\d]/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** Switching a field's value source resets the settings that no longer apply, so a stale value can never ride along. */
export function withSource(f: UiField, source: UiField['source']): UiField {
  const next: UiField = { ...f, source };
  delete next.profileKey; delete next.fixedValue; delete next.locked; delete next.startingValue;
  // The HR date format is not available for a value the employee types, or one copied from the record: back to plain text.
  if (f.kind === 'text' && f.text?.validation === 'dateUS' && (source === 'employee' || source === 'profile')) next.text = { ...f.text, validation: 'none' };
  if (source === 'profile') next.profileKey = 'employeeName';
  if (source === 'fixed') next.fixedValue = f.fixedValue ?? '';
  if (source !== 'employee' && f.kind === 'text') {
    next.locked = true; next.required = true;   // Oct 18 D1: every value HR sets is read-only for the employee, by construction
    // A several-line box can only be filled in by the employee (server rule), so it goes back to one line.
    if (f.text?.multiline) next.text = { ...f.text, multiline: false };
  }
  return next;
}

// ── import review (mirrors the server's rule; the server re-checks) ─────────────────────────────────────────────────────────
function overlapFraction(item: Box, repl: Box): number {
  if (!boxesOverlap(item, repl)) return 0;
  const w = Math.min(item.x + item.width, repl.x + repl.width) - Math.max(item.x, repl.x);
  const h = Math.min(item.y + item.height, repl.y + repl.height) - Math.max(item.y, repl.y);
  return Math.max(0, w) * Math.max(0, h) / Math.max(1, item.width * item.height);
}
export interface UnresolvedItem { itemId: string; acroName: string; blocking: boolean; message: string }
export function unresolvedImportItems(report: HrImportReport | null, fields: UiField[], resolutions: Record<string, HrImportResolution>): UnresolvedItem[] {
  if (!report) return [];
  const kept = new Set(fields.filter((f) => f.origin?.kind === 'imported' && f.origin.acroName).map((f) => f.origin!.acroName as string));
  const byKey = new Map(fields.map((f) => [f.fieldKey, f]));
  const replacedOk = (item: HrImportReport['items'][number], r: HrImportResolution | undefined): boolean => {
    if (!r || r.kind !== 'replaced') return false;
    const f = byKey.get(r.fieldKey);
    return !!f && !!item.box && f.page === item.page && overlapFraction(item.box, f.box) >= 0.5;
  };
  const out: UnresolvedItem[] = [];
  for (const item of report.items) {
    const r = resolutions[item.id];
    if (item.status === 'unsupported') {
      if (replacedOk(item, r)) continue;
      if (item.blocking) out.push({ itemId: item.id, acroName: item.acroName, blocking: true, message: `"${item.acroName}" is ${item.calculated ? 'calculated' : 'required in the PDF'} and cannot be imported. Place a field over its original spot (page ${item.page ?? '?'}); leaving it out is not allowed.` });
      else if (!(r && r.kind === 'ack')) out.push({ itemId: item.id, acroName: item.acroName, blocking: false, message: `"${item.acroName}" cannot be imported. Replace it with a field, or confirm it is not needed.` });
    } else if (!kept.has(item.acroName) && item.requiredInPdf && !replacedOk(item, r)) {
      out.push({ itemId: item.id, acroName: item.acroName, blocking: true, message: `"${item.acroName}" was required in the PDF and was removed. Place a field over its original spot (page ${item.page ?? '?'}).` });
    }
  }
  return out;
}

export function describeField(f: UiField): string {
  const bits: string[] = [KIND_LABEL[f.kind]];
  if (f.kind === 'radio' && f.radio) bits.push(`group ${f.radio.groupKey}`);
  if (f.required) bits.push('required');
  if (f.source === 'profile') bits.push(f.profileKey === 'employeeWorkEmail' ? 'work email filled in' : 'name filled in');
  if (f.source === 'assignment') bits.push('HR types when assigning');
  if (f.source === 'fixed') bits.push('fixed text');
  if (f.source !== 'employee') bits.push(legacyValueProblem(f) ? 'NEEDS REVIEW' : 'read-only for the employee');
  if (f.source === 'employee' && (f.startingValue || f.dropdown?.defaultValue)) bits.push('starts with an answer the employee can change');
  return bits.join(' · ');
}

/** Values HR must type at assignment: which prompts are still empty. */
export function missingAssignmentValues(prompts: Array<{ fieldKey: string; label: string; required: boolean; validation?: string | null }>, values: Record<string, string>): string[] {
  // An HR-entered date that is not a real MM/DD/YYYY date also blocks Assign (the server re-checks).
  const badDates = prompts.filter((p) => p.validation === 'dateUS' && (values[p.fieldKey] ?? '').trim() && !isRealUsDate((values[p.fieldKey] ?? '').trim())).map((p) => `${p.label} (a real date as MM/DD/YYYY)`);
  if (badDates.length) return [...badDates, ...prompts.filter((p) => p.required && !(values[p.fieldKey] ?? '').trim()).map((p) => p.label)];
  return prompts.filter((p) => p.required && !(values[p.fieldKey] ?? '').trim()).map((p) => p.label);
}
